/**
 * Offering the reader the time zone their browser is in.
 *
 * The 复习 settings page counts a reader's day on a zone they choose, and the
 * choice is a list of four hundred names. Somebody who has never opened the
 * page is on `UTC`, which is nobody's day, and finding `Asia/Shanghai` in the
 * list is a scroll through most of the world.
 *
 * **It is an offer and never a change.** The note appears, and the reader
 * presses it or does not. A page that quietly wrote the browser's zone into
 * the select would move the day boundary of somebody on a borrowed computer, a
 * VPN or an aeroplane, and a schedule already written against the old boundary
 * would not move with it. It also would not survive a Save, which is the
 * moment a hint becomes a decision.
 *
 * **Nothing here runs anywhere else.** The note is drawn by
 * `src/user/fuxi-settings-rows.ts` and by nothing else, so a page without it
 * leaves this function after one query. That is the shape every module in this
 * bundle takes, and `src/site/fuxi-keys.ts` is the one next door.
 *
 * **The form this sits in is the region a save replaces.** htmx swaps the
 * whole form on `outerHTML`, so the select and the note that arrive have never
 * been seen here and the button that was bound went out with the old markup.
 * `src/site/htmx-rebinding.ts` calls this again over what arrived, which is
 * why it takes somewhere to look and why the binding is guarded by
 * `boundOnce`.
 */

import { boundOnce } from "./bound-once.js";

/** The note the offer is drawn into, hidden until there is one to make. */
const offer = "[data-cb-fuxi-zone-offer]";

/** Where the zone's own name goes inside it. */
const named = "[data-cb-fuxi-zone-name]";

/** The button that takes the offer. */
const take = "[data-cb-fuxi-zone-take]";

/** The select the offer is about. */
const chosen = "select[name='timeZone']";

/** The buttons this bundle has not already bound. See `boundOnce`. */
const notYetListening = boundOnce();

/**
 * The zone this browser thinks it is in, where it will say.
 *
 * `resolvedOptions` answers with the host's zone, and a browser that has been
 * told to lie about it answers with the lie, which is the reader's business
 * rather than this page's.
 */
function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "";
  }
}

/**
 * Show the offer, and press it into the select when the reader takes it.
 *
 * The name is the whole of what says this is the note, so a note without one
 * is markup this page did not draw and is left alone. The button is bound
 * through `notYetListening`, which is what keeps a second pass over markup
 * already in the page from binding it twice.
 */
function makeOffer(properties: {
  note: HTMLElement;
  select: HTMLSelectElement;
  zone: string;
}): void {
  const { note, select, zone } = properties;
  const name = note.querySelector(named);

  if (name === null) {
    return;
  }

  name.textContent = zone;
  note.hidden = false;

  for (const button of notYetListening(note, take)) {
    button.addEventListener("click", () => {
      select.value = zone;
      // Setting `value` fires nothing, and the page's unsaved line listens for
      // a change. See `./fuxi-settings-unsaved.ts`.
      select.dispatchEvent(new Event("change", { bubbles: true }));
      note.hidden = true;
    });
  }
}

/**
 * Offer the browser's own zone where it is not the one already chosen.
 *
 * The offer is made only for a zone the select actually holds, which is every
 * zone the handler's runtime knows. A browser answering with a name this site
 * cannot store is a browser whose reader is left to choose for themselves.
 *
 * @param page - The page the bundle is running on.
 * @param within - Where to look, which is the whole page unless htmx has just
 * brought a form in.
 */
export function startFuxiZone(page: Document, within: ParentNode = page): void {
  const note = within.querySelector(offer);
  const select = within.querySelector(chosen);

  if (
    !(note instanceof HTMLElement) ||
    !(select instanceof HTMLSelectElement)
  ) {
    return;
  }

  const zone = browserZone();
  const holds = [...select.options].some((option) => option.value === zone);

  if (zone === "" || !holds || zone === select.value) {
    return;
  }

  makeOffer({ note, select, zone });
}
